import React from 'react';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { Check, Zap, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SERVICE_TIERS } from '../../convex/constants/serviceTiers';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const TIER_PRICES = {
  pro: {
    monthly: "$9.95/month",
    yearly: "$79.95/year",
    regularMonthly: "$24.95/month",
    regularYearly: "$249.50/year",
  },
  enterprise: {
    monthly: "$49.95/month",
    yearly: "$379/year",
    regularMonthly: "$110/month",
    regularYearly: "$1,100/year",
  },
};

export function Pricing() {
  const { isSignedIn } = useUser();
  const [billingInterval, setBillingInterval] = React.useState<'monthly' | 'yearly'>('monthly');

  // Calculate savings percentage for display
  const calculateSavings = (tier: 'pro' | 'enterprise') => {
    const regular = billingInterval === 'monthly'
      ? parseFloat(TIER_PRICES[tier].regularMonthly.replace('$', '').replace('/month', ''))
      : parseFloat(TIER_PRICES[tier].regularYearly.replace('$', '').replace('/year', ''));
      
    const current = billingInterval === 'monthly'
      ? parseFloat(TIER_PRICES[tier].monthly.replace('$', '').replace('/month', ''))
      : parseFloat(TIER_PRICES[tier].yearly.replace('$', '').replace('/year', ''));
      
    const savingsPercent = Math.round((1 - (current / regular)) * 100);
    
    return `${savingsPercent}%`;
  };

  return (
    <div className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-base text-dogblue font-semibold tracking-wide uppercase">
            Pricing
          </h2>
          <p className="mt-2 text-4xl font-extrabold text-gray-900 sm:text-5xl">
            Scale your content creation
          </p>
          <p className="mt-4 text-xl text-gray-600 mb-2">
            {isSignedIn 
              ? 'Upgrade or downgrade your subscription at any time'
              : 'Scale your content creation with our flexible pricing options'
            }
          </p>
          <div className="max-w-lg mx-auto">
            <div className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg mb-6 text-sm">
              <span className="font-semibold">🚀 Founder Launch Pricing:</span> For a limited time, enjoy up to 80% off our regular price!
            </div>
          </div>

          {/* Promo code notice */}
          <div className="mt-4 mb-6">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-md border border-blue-200">
              <span className="font-bold">Use code FOUNDER25</span> at checkout for a free 30-day trial!
            </div>
          </div>
          
          <div className="mt-6 flex justify-center">
            <div className="relative flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`${
                  billingInterval === 'monthly'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-200'
                } relative w-32 rounded-md py-2 text-sm font-medium transition-all duration-200`}
              >
                Monthly billing
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`${
                  billingInterval === 'yearly'
                    ? 'bg-white shadow-sm'
                    : 'hover:bg-gray-200'
                } relative w-32 rounded-md py-2 text-sm font-medium transition-all duration-200`}
              >
                Yearly billing
                <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-bold bg-green-500 text-white rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-8 lg:gap-6 sm:grid-cols-2 md:grid-cols-2 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-auto">
          {(['pro', 'enterprise'] as const).map((tier) => {
            const savings = calculateSavings(tier);
            return (
              <motion.div 
                key={tier} 
                className="rounded-xl shadow-lg overflow-hidden border border-gray-100 bg-white relative"
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)"
                }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                {/* Founder pricing banner */}
                <div className="bg-gradient-to-r from-dogbrown to-amber-600 text-white py-1 text-center">
                  <span className="text-xs font-bold uppercase tracking-wider">Founder Launch Pricing</span>
                </div>
                
                <div className="p-6 pt-8">
                  <div className="flex items-start">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        {tier === 'pro' ? (
                          <Zap className="w-5 h-5 text-dogblue" />
                        ) : (
                          <Crown className="w-5 h-5 text-dogbrown" />
                        )}
                        {SERVICE_TIERS[tier].name}
                      </h3>
                      
                      <div className="mt-4">
                        <div className="flex flex-col">
                          <div className="flex items-baseline">
                            <span className="text-4xl font-extrabold text-gray-900">
                              {TIER_PRICES[tier][billingInterval].split('/')[0]}
                            </span>
                            <span className="text-base font-medium text-gray-500 ml-1">
                              {billingInterval === 'yearly' ? '/year' : '/month'}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <s className="text-gray-400 text-sm">Regular: {billingInterval === 'yearly' ? 
                              TIER_PRICES[tier].regularYearly : 
                              TIER_PRICES[tier].regularMonthly}
                            </s>
                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                              Save {savings}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                    }`}>
                      <span className={`text-2xl font-bold ${
                        tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        {tier === 'pro' ? 'P' : 'E'}
                      </span>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3">
                    <li className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                        }`} />
                      </div>
                      <span className="text-gray-700 text-sm">{SERVICE_TIERS[tier].monthlyLimit} posts per month</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                        }`} />
                      </div>
                      <span className="text-gray-700 text-sm">
                        {SERVICE_TIERS[tier].features.maxWordCount} words per post
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                        }`} />
                      </div>
                      <span className="text-gray-700 text-sm">
                        {tier === 'pro' 
                          ? 'Access to advanced AI models' 
                          : 'Access to all AI models'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                        }`} />
                      </div>
                      <span className="text-gray-700 text-sm">Custom writing styles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                        }`} />
                      </div>
                      <span className="text-gray-700 text-sm">Strike list feature</span>
                    </li>
                    {SERVICE_TIERS[tier].features.marketerMode && (
                      <li className="flex items-start gap-2">
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                          tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          <Check className={`w-3 h-3 ${
                            tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                          }`} />
                        </div>
                        <span className="text-gray-700 text-sm">Marketer mode</span>
                      </li>
                    )}
                    {SERVICE_TIERS[tier].features.prioritySupport && (
                      <li className="flex items-start gap-2">
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
                          tier === 'pro' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          <Check className={`w-3 h-3 ${
                            tier === 'pro' ? 'text-blue-700' : 'text-amber-700'
                          }`} />
                        </div>
                        <span className="text-gray-700 text-sm">Priority support</span>
                      </li>
                    )}
                  </ul>

                  {!isSignedIn ? (
                    <SignInButton mode="modal">
                      <Button className={`mt-8 w-full ${
                        tier === 'pro' 
                          ? 'bg-dogblue hover:bg-dogblue/90' 
                          : 'bg-dogbrown hover:bg-dogbrown/90'
                      } text-white`}>
                        Sign in to upgrade
                      </Button>
                    </SignInButton>
                  ) : (
                    <Link to="/upgrade">
                      <Button className={`mt-8 w-full ${
                        tier === 'pro' 
                          ? 'bg-dogblue hover:bg-dogblue/90' 
                          : 'bg-dogbrown hover:bg-dogbrown/90'
                      } text-white`}>
                        Upgrade to {SERVICE_TIERS[tier].name}
                      </Button>
                    </Link>
                  )}
                </div>
                <div className="py-3 px-6 bg-gray-50 text-gray-500 text-xs text-center">
                  <span className="italic">Limited time offer - regular price will apply after launch period</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}